import React, {useCallback, useEffect, useState} from "react";
import {Card, ListGroup} from "react-bootstrap";
import {Location, NewsItem, User} from "../../types/Types";
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db, functions} from "../../../firebase/FirebaseProvider";
import {httpsCallable} from "@firebase/functions";
import {NOTIFICATION_LIMIT, logDataLoaded} from "../../util/Util";
import UserNav from "../UserNav";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import CommentRow from "../../locations/profile/CommentRow";
import PageTracking from "../../auth/PageTracking";
import {useParams} from "react-router-dom";
import {V2GlassPanel} from "../../shared/v2/V2Glass";
import V2CommentRow from "../../shared/v2/V2CommentRow";

interface Props {
    currentUser: User | undefined
    likesMap: Map<string, boolean>
    locations: Array<Location> | undefined
}

function UserComments(props: Props) {
    const { id } = useParams<{ id: string }>();

    const [user, setUser] = useState<User | undefined>(undefined);
    const [comments, setComments] = useState<Array<NewsItem> | undefined>(undefined);
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const updateCommentLikeCount = useCallback((commentId: string, adjustment: number) => {
        setComments(prevComments => {
            if (!prevComments) return prevComments;
            return prevComments.map(comment => {
                if (comment.id === commentId) {
                    return {
                        ...comment,
                        likeCount: (comment.likeCount || 0) + adjustment
                    };
                }
                return comment;
            });
        });
    }, []);

    const loadComments = useCallback(async() => {
        if(!user) {
            return;
        }

        let comments: Array<NewsItem> = [];
        const commentsOnceCollection = query(collection(db, 'comments'),
            where('userId', '==', user.id),
            where('hidden', '==', false),
            orderBy('lastModified', 'desc'),
            limit(NOTIFICATION_LIMIT));
        const commentsOnceCollectionSnapshot = await getDocs(commentsOnceCollection);
        commentsOnceCollectionSnapshot.forEach((doc) => {
            let tempComment = doc.data() as NewsItem;
            tempComment.id = doc.id;
            comments.push(tempComment);
        });
        logDataLoaded('user comments', comments.length);
        setComments(comments);
    }, [user]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                let userToSet = docSnap.data() as User;
                userToSet.id = docSnap.id;
                setUser(userToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user) return;
        loadComments();
    }, [user, loadComments]);

    const hideComment = useCallback(async (commentId: string) => {
        const hideCommentApi = httpsCallable(functions, 'hideComment');
        await hideCommentApi(
            {
                commentId
            }
        )
        await loadComments();
    }, [loadComments]);

    return (
        <>
            <PageTracking />
            {props.currentUser && (isUiV2 ? <UserDetailsV2 currentUser={props.currentUser} user={user} locations={props.locations} /> : <UserDetails user={user} />)}
            {props.currentUser && user && !isUiV2 && <UserNav currentUser={props.currentUser} user={user} />}
            {props.currentUser && (isUiV2 ? <V2GlassPanel className="user-v2-comments-list-card v2-glass-list-panel">
                {!comments && <div className="user-v2-empty-list">Loading...</div>}
                {comments && comments.length === 0 && <div className="user-v2-empty-list">No comments yet</div>}
                {comments && comments.map((comment: NewsItem) => (
                    <V2CommentRow
                        key={comment.id}
                        comment={comment}
                        user={user}
                        session={undefined}
                        currentUser={props.currentUser}
                        locationName={props.locations?.find((location) => location.id === comment.locationId)?.name || comment.locationName}
                        likesMap={props.likesMap}
                        onLikeCountChange={updateCommentLikeCount}
                        canDelete={props.currentUser!.id === comment.userId || !!props.currentUser!.admin}
                        onDelete={hideComment}
                    />
                ))}
            </V2GlassPanel> : <Card className="mb-3">
                {!isUiV2 && comments && comments.length > 0 && <Card.Header className={"header-padding"}>
                    <div className={"d-inline-block stats-card float-left"}>
                        Comments: <b>{comments.length}</b>
                    </div>
                </Card.Header>}
                <ListGroup variant="flush">
                    {!comments && <div className={'loading'}>
                        Loading...
                    </div>}
                    {(comments && comments.length === 0) &&<div className={'loading'}>
                        No comments yet
                    </div>}
                    {comments && comments
                        .map((comment: NewsItem, index: number) => (
                        props.currentUser && <CommentRow 
                            key={comment.id} 
                            user={props.currentUser} 
                            comment={comment} 
                            deleteComment={() => hideComment(comment.id)} 
                            commentUser={user}
                            likesMap={props.likesMap}
                            onLikeCountChange={updateCommentLikeCount}
                        />
                    ))}
                </ListGroup>
            </Card>)}
        </>
    )
}

export default UserComments;
